import Div from 'components/Div';
import React from 'react';
import { NotificationManager } from 'react-notifications';
import styled from 'styled-components';
import Req from 'utils/Request';
import PizzaInstance from '../Pizza/Instance';
import Cardbox from '../Cardbox';
import FlexDivider from '../FlexDivider';

const getGroupItems = (response) => Object.values(response?.data?.data ?? {});
const getItemId = (item) => item?.menuItemDefID ?? item?.menuItemDefId ?? item?.id;
const isMainGroup = (group) => (group?.type ?? group?.groupType) === 'main';
const isAdditionalPriceGroup = (group) => `${group?.name ?? group?.optionName ?? ''}`.trim().toLowerCase() === 'нэмэлт үнэ';

const ComboPopup = ({ instance, setSelectedCombo }) => {
    const [choiceData, setChoiceData] = React.useState({});
    const [selectedChoiceData, setSelectedChoiceData] = React.useState({ mainItem: null, sideItems: {} });
    const instanceRef = React.useRef(instance);
    const groups = React.useMemo(() => instance?.groups ?? [], [instance]);

    React.useEffect(() => {
        instanceRef.current = instance;
    }, [instance]);
    const fetchComboDefs = React.useCallback(async () => {
        if (!groups.length) return;

        try {
            const responses = await Promise.all(
                groups.map((group) =>
                    Req({
                        url: '/menu2/combo/getComboGroupMenusForOrder',
                        body: { groupId: group.id },
                        hideSuccess: true,
                    })
                )
            );
            const mappedChoices = groups.reduce((acc, group, index) => {
                acc[group.id] = getGroupItems(responses[index]);
                return acc;
            }, {});
            setChoiceData(mappedChoices);
        } catch (err) {
            NotificationManager.error(err?.message ?? 'Failed to load combo choices', 'Error', 10000);
        }
    }, [groups]);
    const handleChoice = React.useCallback((group, value) => {
        if (!value || !group?.id) return;

        setSelectedChoiceData((prevState) => {
            if (isMainGroup(group)) {
                if (getItemId(prevState.mainItem) === getItemId(value)) return prevState;
                return { ...prevState, mainItem: value };
            }

            if (getItemId(prevState.sideItems[group.id]) === getItemId(value)) return prevState;

            return {
                ...prevState,
                sideItems: {
                    ...prevState.sideItems,
                    [group.id]: value,
                },
            };
        });
    }, []);

    const isChecked = React.useCallback(
        (group, item) => {
            const selectedItem = isMainGroup(group) ? selectedChoiceData?.mainItem : selectedChoiceData?.sideItems?.[group.id];
            if (!selectedItem) return false;

            const selectedItemId = getItemId(selectedItem);
            if (!selectedItemId) return false;

            if (item?.type === 'pizza') {
                return (item?.definitions ?? []).some((definition) => getItemId(definition) === selectedItemId);
            }

            return getItemId(item) === selectedItemId;
        },
        [selectedChoiceData]
    );

    const isDisabled = React.useCallback(
        (group, item) => {
            if (!isAdditionalPriceGroup(group)) return false;

            const selectedItemIds = new Set();
            const mainItemId = getItemId(selectedChoiceData?.mainItem);
            if (mainItemId && !isMainGroup(group)) selectedItemIds.add(mainItemId);

            Object.entries(selectedChoiceData?.sideItems ?? {}).forEach(([groupId, sideItem]) => {
                if (groupId === `${group.id}`) return;
                const sideItemId = getItemId(sideItem);
                if (sideItemId) selectedItemIds.add(sideItemId);
            });

            if (!selectedItemIds.size) return false;

            if (item?.type === 'pizza') {
                return (item?.definitions ?? []).some((definition) => selectedItemIds.has(getItemId(definition)));
            }

            return selectedItemIds.has(getItemId(item));
        },
        [selectedChoiceData]
    );

    React.useEffect(() => {
        fetchComboDefs();
    }, [fetchComboDefs]);

    React.useEffect(() => {
        if (!groups.length || !Object.keys(choiceData).length) return;

        groups.forEach((group) => {
            const items = choiceData[group.id] ?? [];
            if (!items.length) return;

            const firstItem = items[0];
            if (firstItem?.type === 'pizza') {
                const firstDef = firstItem?.definitions?.[0];
                if (firstDef) handleChoice(group, firstDef);
            } else {
                const firstDef = firstItem?.definitions?.[0];
                if (firstDef) handleChoice(group, firstDef);
            }
        });
    }, [choiceData, groups, handleChoice]);

    React.useEffect(() => {
        if (typeof setSelectedCombo !== 'function') return;

        const currentInstance = instanceRef.current;
        const currentGroups = currentInstance?.groups ?? [];
        const mainGroup = currentGroups.find(isMainGroup) ?? null;
        const sideGroups = currentGroups.filter((group) => !isMainGroup(group));
        setSelectedCombo({
            ...currentInstance,
            comboMainItems: mainGroup
                ? {
                    ...mainGroup,
                    selected: selectedChoiceData.mainItem
                        ? { ...selectedChoiceData.mainItem, groupId: mainGroup.id }
                        : null,
                }
                : null,
            comboSideItems: sideGroups.map((group) => ({
                ...group,
                selected: selectedChoiceData.sideItems[group.id]
                    ? { ...selectedChoiceData.sideItems[group.id], groupId: group.id }
                    : null,
            })),
        });
    }, [selectedChoiceData, setSelectedCombo]);
    return (
        <Container>
            {groups.map((group) => {
                const items = choiceData[group.id] ?? [];
                return (
                    <Div c="choiceWrap" key={group.id}>
                        <Div c="choiceType">
                            {group?.name} - {group?.groupType}
                        </Div>

                        {items.length ? (
                            <Div c="itemsWrap">
                                <FlexDivider grid={Object.keys(items)?.length <= 3 ? 3 : 3} gap={20}>
                                    {items.map((item, index) => {
                                        if (item.type === 'pizza') {
                                            const checked = isChecked(group, item);
                                            const disabled = isDisabled(group, item);
                                            return (
                                                <PizzaInstance
                                                    isPizzaTab={false}
                                                    key={item.id}
                                                    name={item.masterName}
                                                    data={item}
                                                    checked={checked}
                                                    isCombo
                                                    disabled={disabled}
                                                    onChange={(value) => handleChoice(group, value)}
                                                />
                                            );
                                        }
                                        return item.definitions.map((def) => (
                                            <Cardbox
                                                key={def.id}
                                                name={def.masterName}
                                                price={item?.price || item?.defaultPrice}
                                                desc={item.description}
                                                imgUrl={item?.imageUrl}
                                                img={item?.name?.toLowerCase()}
                                                data={def}
                                                isCombo
                                                checked={isChecked(group, def)}
                                                disabled={isDisabled(group, def)}
                                                onSelect={(value) => handleChoice(group, value)}
                                            />
                                        ));
                                    })}
                                </FlexDivider>
                            </Div>
                        ) : (
                            <Div c="emptyChoice">No choices available.</Div>
                        )}
                    </Div>
                );
            })}
        </Container >
    );
};

export default ComboPopup;

const Container = styled.div`
    .choiceWrap {
        margin-bottom: 30px;
        .choiceType {
            font-size: 20px;
            opacity: 0.5;
            font-weight: 500;
            text-transform: capitalize;
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
            padding-bottom: 15px;
            margin-bottom: 15px;
        }
        .emptyChoice {
            opacity: 0.6;
        }
    }
`;
